import { useOrganizationList } from '@clerk/clerk-react';
import { UserOrganizationInvitationResource } from '@clerk/types';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClerkWrapper } from '../../../components';
import { PageURL } from '../../../router/pageURL';
import { NewSignupPageSectionEnum } from '../enums';
import { useAuth, useIsBrokerOnboardingNewFlowEnabled } from 'providers';
import { useQueryClient } from '@tanstack/react-query';
import { BROKER_ONBOARDING_QUERY_KEY } from 'queries/broker/useBrokerOnboarding/useBrokerOnboarding';

export const SelectOrganisation = () => {
  const { updateUser } = useAuth();
  const isBrokerOnboardingNewFlowEnabled = useIsBrokerOnboardingNewFlowEnabled();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    userMemberships: { data: memberships, isLoading: isUserMembershipsLoading },
    userInvitations: { data: invitations, isLoading: isUserInvitationsLoading },
    setActive,
  } = useOrganizationList({
    userMemberships: {
      pageSize: 5,
    },
    userInvitations: {
      pageSize: 5,
    },
  });

  useEffect(() => {
    if (isUserInvitationsLoading || isUserMembershipsLoading) return;

    if (!memberships?.length && !invitations?.length) {
      createOrganisation();
    }
  }, [memberships, invitations, isUserInvitationsLoading, isUserMembershipsLoading]);

  const createOrganisation = () => {
    if (isBrokerOnboardingNewFlowEnabled) {
      queryClient.invalidateQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY] });
      queryClient.refetchQueries({ queryKey: [BROKER_ONBOARDING_QUERY_KEY], type: 'active' });
      return;
    }

    searchParams.set('section', NewSignupPageSectionEnum.CREATE_ORGANISATION);

    setSearchParams(searchParams);
  };

  const setOrganisation = async (orgId: string) => {
    if (setActive) {
      await setActive({ organization: orgId });
      await updateUser?.({
        unsafeMetadata: {
          currentSignUpStep: NewSignupPageSectionEnum.COMPLETED,
        },
      });

      navigate(PageURL.ROOT);
    }
  };

  const acceptInvitation = async (inv: UserOrganizationInvitationResource) => {
    await inv.accept();
    await setOrganisation(inv.publicOrganizationData.id);
  };

  return (
    <ClerkWrapper width='xs'>
      <h2 className='text-md font-semibold mb-4 text-center'>
        <FormattedMessage id={'auth.signup.chooseOrganisation.title'} />
      </h2>

      <div className='w-full'>
        <div>
          {memberships?.map(membership => (
            <div
              key={membership.id}
              className='border-solid border-y border-gray-200 border-x-0 w-full flex items-center justify-between py-3 px-8 hover:bg-gray-50 cursor-pointer transition-all duration-150'
              onClick={() => setOrganisation(membership.organization.id)}
            >
              <span className='text-gray-900 font-medium text-sm'>{membership.organization.name}</span>
            </div>
          ))}
        </div>

        <div className='w-full'>
          {invitations?.map(invitation => (
            <div
              key={invitation.id}
              className='border-solid border-y border-gray-200 border-x-0 last:border-b-0 w-full flex items-center justify-between py-3 px-8 hover:bg-gray-50 cursor-pointer transition-all duration-150'
              onClick={() => acceptInvitation(invitation)}
            >
              <span className='text-gray-900 font-medium text-sm'>{invitation.publicOrganizationData.name}</span>
            </div>
          ))}
        </div>

        <div
          className='border-solid border-y border-gray-200 border-x-0 w-full flex items-center justify-center p-3 hover:bg-gray-50 cursor-pointer transition-all duration-150 '
          onClick={createOrganisation}
        >
          <div className='flex items-center'>
            <div className='w-5 h-5 rounded-full flex items-center justify-center mr-3'>
              <span className='text-gray-400 text-sm font-light'>+</span>
            </div>
            <span className='text-gray-500 font-medium text-sm tracking-wide'>CREATE ORGANISATION</span>
          </div>
        </div>
      </div>
    </ClerkWrapper>
  );
};
